import EmptyState from '@/components/EmptyState';
import { AiDock } from '@/features/ai-changeset/components/AiDock';
import { runPositions } from '@/features/ai-changeset/utils/changeset-view';
import { PromotionRow } from '@/features/promotions/components/PromotionRow';
import {
  useArchivePromotion,
  usePromotions,
  type PromotionListStatus,
} from '@/features/promotions/hooks/usePromotions';
import {
  useCanUsePromotionsAi,
  usePromotionsQuickEdit,
} from '@/features/promotions/hooks/usePromotionsQuickEdit';
import { useI18nStore, useT } from '@/i18n';
import { useRestaurantStore, useRestaurantTimezone } from '@/stores/restaurant.store';
import type { Promotion } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  List,
  Navbar,
  NavRight,
  Link as F7Link,
  Page,
  Preloader,
  Segmented,
  Button,
  f7,
} from 'framework7-react';
import { useState } from 'react';

function PromotionsListPage() {
  const t = useT();
  const tp = t.promotions;
  const now = Date.now();
  const [tab, setTab] = useState<PromotionListStatus>('active');
  const { data: promotions, isLoading, error } = usePromotions(tab);
  const canUseAi = useCanUsePromotionsAi();
  const activeTabEmpty = tab === 'active' && promotions?.length === 0;
  const { data: everyPromotion } = usePromotions(
    'all',
    tab === 'active' && (activeTabEmpty || canUseAi),
  );
  const restaurantHasNone = tab === 'all' || everyPromotion?.length === 0;
  const quickEdit = usePromotionsQuickEdit(promotions, tab === 'all' ? promotions : everyPromotion);
  const { draft } = quickEdit;
  const runs = runPositions(draft.rows.map((row) => row.status !== null));
  const archive = useArchivePromotion();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const timezone = useRestaurantTimezone();
  const locale = useI18nStore((s) => s.locale);
  const names = new Map((everyPromotion ?? []).map((p) => [p.id, p.name.en ?? '']));
  for (const row of draft.rows) names.set(row.id, row.value.name.en ?? '');

  const confirmArchive = (p: Promotion) => {
    f7.dialog.confirm(tp.archiveConfirm(p.name.en), tp.archiveTitle, () => {
      archive.mutate(p.id, {
        onSuccess: () =>
          f7.toast.show({ text: tp.archived, closeTimeout: 2000, position: 'bottom' }),
      });
    });
  };

  return (
    <Page name="promotions">
      <Navbar title={tp.title} backLink={t.back}>
        <NavRight>
          <F7Link iconF7="plus" href="/promotions/new" />
        </NavRight>
      </Navbar>

      {quickEdit.canUseAi && (
        <AiDock
          dock={quickEdit}
          testId="promotions-ai-dock"
          title={t.aiDock.promotionsTitle}
          placeholder={t.promotionsQuickEdit.placeholder}
        />
      )}

      <Block>
        <Segmented strong>
          <Button active={tab === 'active'} onClick={() => setTab('active')}>
            {tp.active}
          </Button>
          <Button active={tab === 'all'} onClick={() => setTab('all')}>
            {tp.all}
          </Button>
        </Segmented>
      </Block>

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title={tp.couldNotLoadError}
          description={(error as Error).message}
        />
      )}

      {promotions &&
        draft.rows.length === 0 &&
        (restaurantHasNone ? (
          <EmptyState icon="tag" title={tp.noneYet} description={tp.noneYetDescription} />
        ) : (
          <EmptyState icon="tag" title={tp.noneRunning} description={tp.noneRunningDescription} />
        ))}

      {draft.rows.length > 0 && (
        <List strongIos insetIos dividersIos mediaList>
          {draft.rows.map((row, index) => {
            const promotion = row.promotion;
            return PromotionRow({
              row,
              run: runs[index] ?? null,
              store: quickEdit.navigation.store,
              t,
              context: { currency, timezone, locale, now, names },
              onArchive:
                promotion?.isActive && row.status === null ? () => confirmArchive(promotion) : null,
            });
          })}
        </List>
      )}

      <BlockTitle medium>{tp.howWorks}</BlockTitle>
      <Block strong inset>
        <p>{tp.howWorksDescription}</p>
      </Block>
    </Page>
  );
}

export default PromotionsListPage;
